import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { store } from "./redux/store";
import { checkAuth } from "./redux/slices/authSlice";
import { DashboardLayout } from "./components/layout";
import { ProtectedRoute } from "./routes/ProtectedRoute";
import { ToastContainer } from "./components/common/ToastContainer";

// Pages
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { DashboardPage } from "./pages/DashboardPage";
import { JobsPage } from "./pages/JobsPage";
import { CreateJobPage } from "./pages/CreateJobPage";
import { JobDetailsPage } from "./pages/JobDetailsPage";
import { AdminDashboardPage } from "./pages/AdminDashboardPage";
import { AdminUsersPage } from "./pages/AdminUsersPage";
import { AdminJobsPage } from "./pages/AdminJobsPage";
import { AdminChatsPage } from "./pages/admin/AdminChatsPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { LiveDemoPage } from "./pages/LiveDemoPage";
import { ChatPage } from "./pages/ChatPage";
import { SettingsAccountPage } from "./pages/SettingsAccountPage";
import { SettingsApiKeysPage } from "./pages/SettingsApiKeysPage";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Dashboard layout wrapper for authenticated routes
const AuthenticatedLayout: React.FC = () => {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
};

// Protected wrapper for user routes
const UserRoutes: React.FC = () => {
  return (
    <ProtectedRoute requireUser>
      <AuthenticatedLayout />
    </ProtectedRoute>
  );
};

// Protected wrapper for shared routes (any authenticated user)
const SharedRoutes: React.FC = () => {
  return (
    <ProtectedRoute>
      <AuthenticatedLayout />
    </ProtectedRoute>
  );
};

// Protected wrapper for admin routes
const AdminRoutes: React.FC = () => {
  return (
    <ProtectedRoute requireAdmin>
      <AuthenticatedLayout />
    </ProtectedRoute>
  );
};

const AppContent: React.FC = () => {
  useEffect(() => {
    store.dispatch(checkAuth());
  }, []);

  return (
    <Router>
      <Routes>
        {/* ========== PUBLIC ROUTES (No Layout) ========== */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/home" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* ========== SHARED ROUTES (Any Auth User) ========== */}
        <Route element={<SharedRoutes />}>
          <Route path="/live-demo" element={<LiveDemoPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/profile" element={<SettingsAccountPage />} />
          {/* Settings Routes */}
          <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
          <Route path="/settings/account" element={<SettingsAccountPage />} />
          <Route path="/settings/api-keys" element={<SettingsApiKeysPage />} />
        </Route>

        {/* ========== USER ROUTES (User Role Only) ========== */}
        <Route element={<UserRoutes />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/:jobId" element={<JobDetailsPage />} />
          <Route path="/create-job" element={<CreateJobPage />} />
        </Route>

        {/* ========== ADMIN ROUTES (Admin Role Only) ========== */}
        <Route element={<AdminRoutes />}>
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/jobs" element={<AdminJobsPage />} />
          <Route path="/admin/chats" element={<AdminChatsPage />} />
        </Route>

        {/* 404 - No Layout */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      <ToastContainer />
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AppContent />
      </GoogleOAuthProvider>
    </Provider>
  );
};

export default App;
